import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Tooltip from '@liveauctioneers/caterwaul-components/lib/Tooltip/Tooltip';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, H2, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import React from 'react';
import styled, { css } from 'styled-components';

import { CurrencySelection } from '@/components/CurrencySelection/CurrencySelection';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { openExcludeHouseModal } from '@/redux/modules/modal';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { fetchSellersIfNeeded, getHousesById } from '@/redux/modules/seller';
import { Seller as SellerType } from '@/types/Seller';
import { getSellerUrl } from '@/utils/urls';

export const PreferencesForm = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const excludedHouseIds = useAppSelector(getUserExcludedHouses);
    const excludedHouses = useAppSelector((state) => getHousesById(state, excludedHouseIds));

    const showConfirmExcludeModal = React.useCallback(
        (houseId: number) => {
            dispatch(openExcludeHouseModal(houseId, true));
        },
        [dispatch]
    );

    const hasExcludedHouses = Boolean(Array.isArray(excludedHouses) && excludedHouses.length);

    React.useEffect(() => {
        if (excludedHouseIds.length) {
            dispatch(fetchSellersIfNeeded(excludedHouseIds));
        }
    }, [dispatch, excludedHouseIds]);

    const sellerRows = excludedHouses.map((seller: SellerType) => {
        return (
            <StyledGridRow key={seller.sellerId}>
                <AuctionHouseName>
                    <StyledLink
                        route
                        to={getSellerUrl(seller.sellerId, seller.name)}
                    >
                        {seller.name}
                    </StyledLink>
                </AuctionHouseName>
                <IncludeLink>
                    <InlineButton
                        onClick={() => showConfirmExcludeModal(seller.sellerId)}
                        size="sm"
                    >
                        {formatMessage({ id: 'include' })}
                    </InlineButton>
                </IncludeLink>
                <GridRowBottom />
            </StyledGridRow>
        );
    });

    return (
        <GridWrapper>
            {hasExcludedHouses && (
                <StyledGrid>
                    <ExcludedHouses>
                        <FormattedMessage id="excluded_houses" />
                    </ExcludedHouses>
                    <ExcludedMissive>
                        <FormattedMessage id="you_excluded_these" />
                    </ExcludedMissive>
                    <AuctionHouseLabel>
                        <FormattedMessage id="name" />
                    </AuctionHouseLabel>
                    <IncludeLabel>
                        <FormattedMessage id="include" />
                    </IncludeLabel>
                    <GridHeaderBottom />
                </StyledGrid>
            )}
            {sellerRows}
            <StyledSectionTitle
                $hasExcludedHouses={hasExcludedHouses}
                data-testid="sectionTitle"
            >
                <FormattedMessage id="preferredCurrencyConversion.changeCurrency" />
                <Tooltip
                    placement="right"
                    trigger={
                        <Info
                            className="ml-2xs inline-block text-surface-500"
                            size="sm"
                            style="fill"
                        />
                    }
                >
                    <FormattedMessage id="preferredCurrencyConversion.popoverSummary" />
                </Tooltip>
            </StyledSectionTitle>
            <CurrencySelection />
        </GridWrapper>
    );
};

export default PreferencesForm;

const GridWrapper = styled.div`
    width: 50%;
    margin: 0 auto 32px 20px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        width: calc(100% - 31px);
        margin: 0 9px 32px 20px;
    }
`;

const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: auto 21px 80px 11px;
    grid-template-rows: 33px 16px minmax(20px, auto) 32px 20px 16px;
    grid-template-areas:
        'title title title .'
        '. . . .'
        'descr descr descr .'
        '. . . .'
        'ahlabel . includelabel .'
        'bottom bottom bottom .';
`;

interface SectionTitleProps {
    $hasExcludedHouses: boolean;
}

const StyledSectionTitle = styled(H2)<SectionTitleProps>`
    display: flex;
    margin: 5px 0 28px 0;
    ${({ $hasExcludedHouses }) =>
        $hasExcludedHouses &&
        css`
            margin: 64px 0 28px 0;
        `}
`;

const StyledGridRow = styled.div`
    display: grid;
    grid-template-columns: auto 21px 80px 11px;
    grid-template-rows: 16px minmax(20px, auto) 16px;
    grid-template-areas:
        '. . . .'
        'ahname . includelink .'
        'bottom bottom bottom .';
`;

const ExcludedHouses = styled(H2)`
    grid-area: title;
    margin-top: 3px;
`;

const ExcludedMissive = styled(BodySecondary)`
    grid-area: descr;
`;

const AuctionHouseLabel = styled(LabelMini)`
    grid-area: ahlabel;
`;

const IncludeLabel = styled(LabelMini)`
    grid-area: includelabel;
`;

const GridHeaderBottom = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey200};
    grid-area: bottom;
`;

const AuctionHouseName = styled(BodySecondary)`
    grid-area: ahname;
`;

const StyledLink = styled(Link)`
    text-decoration: none;
    color: ${({ theme }) => theme.colors.black};
    & :hover {
        text-decoration: underline;
        color: ${({ theme }) => theme.colors.blue200};
    }
`;

const IncludeLink = styled(BodySecondary)`
    grid-area: includelink;
`;

const GridRowBottom = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    grid-area: bottom;
`;
